<template>
  <section class="master-board-page h-full w-full flex flex-col bg-gray-50 min-h-screen">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <!-- Breadcrumb -->
      <nav class="text-sm text-gray-600 mb-6 flex">
        <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
        <span class="mx-1">/</span>
        <router-link to="/tasks" class="hover:text-blue-700 transition font-medium">Task Management</router-link>
        <span class="mx-1">/</span>
        <span v-if="masterBoardData" class="text-blue-900 font-semibold">{{ masterBoardData.name }}</span>
        <span v-else class="text-blue-900 font-semibold">Loading...</span>
      </nav>

      <!-- Loading State -->
      <div v-if="loading" class="space-y-6">
        <div class="bg-white rounded-2xl border shadow-md p-8 animate-pulse">
          <div class="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div class="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
        <div class="bg-white rounded-2xl border shadow-md p-6 animate-pulse">
          <div class="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div v-for="i in 4" :key="i" class="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>

      <!-- Not Found -->
      <div v-else-if="!masterBoardData && !loading" class="text-center py-16">
        <p class="text-gray-600">Master board not found</p>
        <router-link to="/tasks" class="mt-4 inline-block text-primary hover:underline">Back to Task
          Management</router-link>
      </div>

      <!-- Content -->
      <template v-else-if="masterBoardData">
        <!-- Header -->
        <div class="bg-white border border-gray-200 rounded-2xl shadow-md overflow-hidden mb-6">
          <div class="px-6 py-6 flex items-start justify-between gap-4"
            :style="{ borderLeft: `4px solid ${masterBoardData.color || '#499DD4'}` }">
            <div class="min-w-0 flex-1">
              <h1 class="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <div class="w-2 h-10 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: masterBoardData.color || '#499DD4' }"></div>
                {{ masterBoardData.name }}
              </h1>
              <p v-if="masterBoardData.description" class="text-gray-600 mt-2 text-base leading-relaxed">
                {{ masterBoardData.description }}
              </p>
              <p v-if="masterBoardData.created_by && masterBoardData.created_by.name"
                class="text-sm text-gray-500 mt-2">
                Created by <span class="font-medium text-gray-700">{{ masterBoardData.created_by.name }}</span>
              </p>
            </div>
            <div v-if="canEditMasterBoard" class="flex-shrink-0">
              <button @click="openEditModal"
                class="px-4 py-2 rounded-lg hover:bg-gray-50 border border-gray-200 text-gray-600 hover:text-gray-800 transition-colors flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
            </div>
          </div>
        </div>

        <!-- View switcher: Overview / Charts -->
        <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-2 mb-6 flex flex-wrap items-center gap-2">
          <button type="button" @click="pageView = 'overview'" :class="[
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2',
            pageView === 'overview'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Overview
          </button>
          <button type="button" @click="pageView = 'charts'" :class="[
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2',
            pageView === 'charts'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
            Charts
          </button>
        </div>

        <!-- Charts -->
        <master-board-charts v-if="pageView === 'charts'" :master-board-id="masterBoardData.id" />

        <!-- Overview -->
        <template v-if="pageView === 'overview'">
          <!-- Members Section -->
          <div class="bg-white rounded-2xl border shadow-lg p-6 mb-6">
            <div class="flex items-center gap-3 mb-4">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h2 class="text-lg font-semibold text-gray-900">Members</h2>
              <span class="text-sm text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                {{ members.length }} {{ members.length === 1 ? 'member' : 'members' }}
              </span>
            </div>
            <div v-if="members.length === 0" class="py-8 text-center text-gray-500">
              <p class="text-sm">No members yet.</p>
            </div>
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div v-for="member in members" :key="member.id" title="Open profile in new tab"
                @click="goToProfile(member)"
                class="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-blue-200 hover:bg-gray-50 transition-all cursor-pointer">
                <div
                  class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img :src="getProfilePicture(member.profile_picture_url)" :alt="member.name"
                    class="w-full h-full object-cover" @error="handleImageError" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="font-medium text-gray-900 truncate">{{ member.name }}</p>
                  <p class="text-xs text-gray-500 truncate">{{ member.email }}</p>
                  <span v-if="isCreator(member)"
                    class="inline-block mt-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 rounded">
                    Creator
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Attachments Archive Section -->
          <div v-if="masterBoardData && parseInt(masterBoardData.id) !== 0"
            class="bg-white rounded-2xl border shadow-lg p-6 mb-6">
            <div class="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div class="flex items-center gap-3">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L21 13" />
                </svg>
                <h2 class="text-lg font-semibold text-gray-900">Attachments Archive</h2>
                <span class="text-sm text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                  {{ attachments.length }} {{ attachments.length === 1 ? 'file' : 'files' }}
                </span>
              </div>
              <div v-if="canUploadToMasterBoard" class="flex items-center gap-2 flex-wrap">
                <button type="button" @click="createAttachmentFolder"
                  class="px-4 py-2 rounded-lg text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-white transition-colors flex items-center gap-2">
                  <icon-folder-plus class="w-4 h-4" />
                  New Folder
                </button>
                <label
                  class="px-4 py-2 rounded-lg text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                  <font-awesome-icon icon="upload" class="h-4 w-4" />
                  Upload
                  <input type="file" ref="masterBoardAttachmentInput" class="hidden"
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                    @change="onMasterBoardAttachmentSelected" />
                </label>
              </div>
            </div>

            <div class="archive-explorer" @contextmenu.prevent="openArchiveExplorerContextMenu($event)">
              <nav class="archive-address-bar">
                <button type="button" @click="navigateAttachmentFolder(null)" class="archive-address-root"
                  title="Attachments Archive">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <span>Attachments Archive</span>
                </button>
                <template v-for="(crumb, crumbIndex) in attachmentBreadcrumb" :key="crumb.id">
                  <span class="archive-address-sep">/</span>
                  <button v-if="crumbIndex < attachmentBreadcrumb.length - 1" type="button"
                    @click="navigateAttachmentFolder(crumb.id)" class="archive-address-crumb">
                    {{ crumb.name }}
                  </button>
                  <span v-else class="archive-address-current">{{ crumb.name }}</span>
                </template>
              </nav>

              <div v-if="masterBoardUploading || masterBoardFolderCreating || archivePasting" class="archive-status">
                <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-500 mx-auto"></div>
                <p class="text-sm mt-2 text-gray-500">{{ archivePastingLabel }}</p>
              </div>
              <div v-else-if="!hasCurrentArchiveItems" class="archive-empty"
                @contextmenu.prevent="openArchiveBackgroundContextMenu($event)">
                <svg class="archive-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p class="text-sm text-gray-500">This folder is empty</p>
                <p class="text-xs text-gray-400 mt-1">Add a folder or upload files to get started</p>
              </div>
              <div v-else class="archive-grid" ref="archiveGrid"
                @mousedown="onArchiveGridMouseDown"
                @contextmenu.prevent="openArchiveExplorerContextMenu($event)">
                <div v-for="folder in currentFolders" :key="'folder-' + folder.id" class="archive-grid-item"
                  data-archive-type="folder"
                  :data-archive-id="folder.id"
                  :class="{
                  'archive-grid-item-selected': isArchiveItemSelected('folder', folder.id),
                  'archive-grid-item-cut': isArchiveItemCut('folder', folder.id),
                }" @click="onArchiveGridItemClick($event, 'folder', folder.id)" @dblclick="openAttachmentFolder(folder)"
                  @contextmenu.prevent="openArchiveContextMenu($event, 'folder', folder)">
                  <button type="button" class="archive-item-kebab-menu"
                    :class="{ 'archive-item-kebab-menu-active': isArchiveContextMenuOpenForItem('folder', folder.id) }"
                    title="More options"
                    @click.stop="openArchiveItemKebabMenu($event, 'folder', folder)">
                    <font-awesome-icon icon="ellipsis-v" class="archive-kebab-icon" />
                  </button>
                  <div class="archive-item-icon-wrap">
                    <img :src="macFolderIcon" alt="" class="archive-folder-icon" />
                  </div>
                  <div class="archive-item-label-wrap">
                    <input v-if="inlineRenamingFolderId && String(inlineRenamingFolderId) === String(folder.id)"
                      :ref="'folderRenameInput-' + folder.id" v-model="inlineRenamingFolderName" type="text"
                      class="archive-item-label-input" @click.stop @dblclick.stop
                      @keydown.enter.prevent="commitInlineRenameAttachmentFolder(folder)"
                      @keydown.esc.prevent="cancelInlineRenameAttachmentFolder"
                      @blur="commitInlineRenameAttachmentFolder(folder)" />
                    <p v-else class="archive-item-label" :title="folder.name"
                      @click.stop="onArchiveLabelClick($event, 'folder', folder)">{{
                        folder.name }}</p>
                  </div>
                  <div v-if="!(inlineRenamingFolderId && String(inlineRenamingFolderId) === String(folder.id))"
                    class="archive-meta-tooltip">
                    <p class="archive-meta-title">{{ folder.name }}</p>
                    <div class="archive-meta-rows">
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Type</span>
                        <span class="archive-meta-value">Folder</span>
                      </div>
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Contains</span>
                        <span class="archive-meta-value">{{ getFolderContentsLabel(folder.id) }}</span>
                      </div>
                      <div v-if="folder.created_by && folder.created_by.name" class="archive-meta-row">
                        <span class="archive-meta-label">Created by</span>
                        <span class="archive-meta-value">{{ folder.created_by.name }}</span>
                      </div>
                      <div v-if="folder.created_at" class="archive-meta-row">
                        <span class="archive-meta-label">Created</span>
                        <span class="archive-meta-value">{{ formatArchiveDateTime(folder.created_at) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-for="att in currentAttachments" :key="'att-' + att.id" class="archive-grid-item"
                  data-archive-type="file"
                  :data-archive-id="att.id"
                  :class="{
                  'archive-grid-item-selected': isArchiveItemSelected('file', att.id),
                  'archive-grid-item-cut': isArchiveItemCut('file', att.id),
                }" @click="onArchiveGridItemClick($event, 'file', att.id)" @dblclick="viewAttachment(att)"
                  @contextmenu.prevent="openArchiveContextMenu($event, 'file', att)">
                  <button type="button" class="archive-item-kebab-menu"
                    :class="{ 'archive-item-kebab-menu-active': isArchiveContextMenuOpenForItem('file', att.id) }"
                    title="More options"
                    @click.stop="openArchiveItemKebabMenu($event, 'file', att)">
                    <font-awesome-icon icon="ellipsis-v" class="archive-kebab-icon" />
                  </button>
                  <div class="archive-item-icon-wrap">
                    <div class="archive-file-icon" :class="getArchiveFileIconClass(att)">
                      <svg class="archive-file-icon-svg" viewBox="0 0 48 56" fill="none">
                        <path
                          d="M8 4C8 2.89543 8.89543 2 10 2H26.5L40 15.5V52C40 53.1046 39.1046 54 38 54H10C8.89543 54 8 53.1046 8 52V4Z"
                          fill="currentColor" fill-opacity="0.15" stroke="currentColor" stroke-width="2" />
                        <path d="M26 2V14C26 15.1046 26.8954 16 28 16H40" stroke="currentColor" stroke-width="2" />
                      </svg>
                      <span class="archive-file-ext">{{ getArchiveFileExtension(att) }}</span>
                    </div>
                  </div>
                  <div class="archive-item-label-wrap">
                    <input v-if="inlineRenamingAttachmentId && String(inlineRenamingAttachmentId) === String(att.id)"
                      :ref="'attachmentRenameInput-' + att.id" v-model="inlineRenamingAttachmentName" type="text"
                      class="archive-item-label-input" @click.stop @dblclick.stop
                      @keydown.enter.prevent="commitInlineRenameAttachment(att)"
                      @keydown.esc.prevent="cancelInlineRenameAttachment" @blur="commitInlineRenameAttachment(att)" />
                    <p v-else class="archive-item-label" :title="getAttachmentDisplayName(att)"
                      @click.stop="onArchiveLabelClick($event, 'file', att)">{{ getAttachmentDisplayName(att) }}</p>
                  </div>
                  <div v-if="!(inlineRenamingAttachmentId && String(inlineRenamingAttachmentId) === String(att.id))"
                    class="archive-meta-tooltip">
                    <p class="archive-meta-title">{{ getAttachmentDisplayName(att) }}</p>
                    <div class="archive-meta-rows">
                      <div v-if="getAttachmentOriginalName(att) !== getAttachmentDisplayName(att)"
                        class="archive-meta-row">
                        <span class="archive-meta-label">Original</span>
                        <span class="archive-meta-value">{{ getAttachmentOriginalName(att) }}</span>
                      </div>
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Type</span>
                        <span class="archive-meta-value">{{ formatArchiveMimeType(att.file_type) }}</span>
                      </div>
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Extension</span>
                        <span class="archive-meta-value">{{ getArchiveFileExtension(att) }}</span>
                      </div>
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Size</span>
                        <span class="archive-meta-value">{{ formatArchiveFileSize(att.file_size) }}</span>
                      </div>
                      <div v-if="att.uploaded_by && att.uploaded_by.name" class="archive-meta-row">
                        <span class="archive-meta-label">Uploaded by</span>
                        <span class="archive-meta-value">{{ att.uploaded_by.name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="archiveMarquee.active" class="archive-marquee" :style="archiveMarqueeStyle"></div>
              </div>

              <div v-if="archiveContextMenu.visible" class="archive-context-menu"
                :style="{ left: archiveContextMenu.x + 'px', top: archiveContextMenu.y + 'px' }" @click.stop
                @contextmenu.prevent>
                <button v-for="action in archiveContextMenuActions" :key="action.id" type="button"
                  class="archive-context-menu-item" :class="{ 'archive-context-menu-item-danger': action.danger }"
                  @click="executeArchiveContextMenuAction(action.id)">
                  <span class="archive-context-menu-icon">
                    <svg v-if="action.id === 'open'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <svg v-else-if="action.id === 'download'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <svg v-else-if="action.id === 'copy'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                    </svg>
                    <icon-cut v-else-if="action.id === 'cut'" class="archive-context-menu-icon-svg" />
                    <icon-folder-plus v-else-if="action.id === 'new-folder'" class="archive-context-menu-icon-svg" />
                    <svg v-else-if="action.id === 'paste'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <svg v-else-if="action.id === 'rename'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <svg v-else-if="action.id === 'delete'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </span>
                  <span class="archive-context-menu-label">{{ action.label }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Sub-Boards Section -->
          <div class="bg-white rounded-2xl border shadow-lg p-6">
            <div class="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div class="flex items-center gap-3">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h2 class="text-lg font-semibold text-gray-900">Sub-Boards</h2>
                <span class="text-sm text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                  {{ subBoards.length }} {{ subBoards.length === 1 ? 'board' : 'boards' }}
                </span>
              </div>
              <button v-if="parseInt(masterBoardData.id) !== 0" @click="openCreateBoardModal"
                class="px-4 py-2 rounded-lg text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-white transition-colors flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                New sub-board
              </button>
            </div>
            <div v-if="subBoards.length === 0" class="py-12 text-center text-gray-500">
              <p class="text-sm">No sub-boards yet.</p>
              <button v-if="parseInt(masterBoardData.id) !== 0" @click="openCreateBoardModal"
                class="mt-4 inline-block px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors">
                Create Your First Sub-Board
              </button>
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div v-for="board in subBoards" :key="board.id" @click="openBoard(board.id)"
                class="bg-gray-50 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer p-5 border-l-4 flex flex-col"
                :style="{ borderLeftColor: board.color || '#499DD4' }">
                <div class="flex items-start justify-between mb-2">
                  <h3 class="text-lg font-semibold text-gray-900 truncate flex-1 min-w-0">{{ board.name }}</h3>
                  <button v-if="canEditBoard(board)" @click.stop="openEditBoard(board)"
                    class="p-2 rounded-lg hover:bg-white border border-gray-200 text-gray-600 hover:text-gray-800 transition-colors flex-shrink-0"
                    title="Edit sub-board">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
                <p v-if="board.created_by && board.created_by.name" class="text-xs text-gray-500 mt-1">
                  Created by {{ board.created_by.name }}
                </p>
                <p v-if="board.description" class="text-gray-600 text-sm mt-2 line-clamp-2">{{ board.description }}</p>
                <div
                  class="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500 flex-wrap gap-x-3 gap-y-1">
                  <span>Total tasks {{ getTaskStats(board).total }}</span>
                  <span>{{ board.members && board.members.length ? board.members.length : 0 }} members</span>
                  <span v-if="board.attachments && board.attachments.length > 0" class="font-medium text-gray-700">
                    {{ board.attachments.length }} {{ board.attachments.length === 1 ? 'attachment' : 'attachments' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Edit Master Board Modal -->
    <transition name="fade" mode="out-in">
      <base-full-modal v-if="showEditModal && masterBoardData" title="Edit Master Board" @close="closeEditModal">
        <edit-master-board-modal class="text-left px-6 py-6" :master-board="masterBoardData" @close="closeEditModal"
          @saved="handleMasterBoardUpdated"></edit-master-board-modal>
      </base-full-modal>
    </transition>

    <!-- Create Sub-Board Modal -->
    <base-full-modal v-if="showCreateBoardModal" title="Create New Sub-Board" @close="closeCreateBoardModal">
      <create-board-modal class="text-left px-6 py-6" :can-view-all-boards="canViewAllBoards"
        :preselected-master-board-id="masterBoardData && masterBoardData.id"
        :preselected-master-board-name="masterBoardData && masterBoardData.name"
        :preselected-master-board-members="members" @close="closeCreateBoardModal"
        @created="handleBoardCreated"></create-board-modal>
    </base-full-modal>

    <!-- Edit Sub-Board Modal -->
    <base-full-modal v-if="showEditBoardModal && selectedBoard" title="Edit Sub-Board" @close="closeEditBoard">
      <edit-board-modal class="text-left px-6 py-6" :board="selectedBoard" :can-view-all-boards="canViewAllBoards"
        @close="closeEditBoard" @saved="handleBoardUpdated"></edit-board-modal>
    </base-full-modal>

    <!-- Attachment Preview Modal -->
    <base-modal v-if="showPreviewModal" title="Attachment Preview" width="90vw" height="85vh"
      @close="closePreviewModal">
      <div class="p-4 flex flex-col attachment-preview-content">
        <div v-if="previewLoading" class="flex items-center justify-center py-12 flex-1">
          <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-500"></div>
        </div>
        <div v-else-if="previewError" class="py-8 text-center text-red-600 flex-1">{{ previewError }}</div>
        <div v-else-if="previewUrl" class="flex justify-center flex-1 min-h-0">
          <img v-if="previewIsImage" :src="previewUrl" class="max-w-full object-contain attachment-preview-img"
            alt="Preview" />
          <iframe v-else-if="previewIsPdf" :src="previewUrl" class="w-full border-0 flex-1 attachment-preview-iframe"
            title="PDF Preview"></iframe>
          <p v-else class="text-gray-500 flex-1">Preview not available for this file type. Use download instead.</p>
        </div>
      </div>
    </base-modal>
  </section>
</template>

<script>
import gql from 'graphql-tag';
import EditMasterBoardModal from './components/EditMasterBoardModal.vue';
import EditBoardModal from './components/EditBoardModal.vue';
import CreateBoardModal from './components/CreateBoardModal.vue';
import MasterBoardCharts from './components/MasterBoardCharts.vue';
import IconCut from '../../components/icons/IconCut.vue';
import IconFolderPlus from '../../components/icons/IconFolderPlus.vue';

export default {
  name: 'master-board-page',
  components: {
    EditMasterBoardModal,
    EditBoardModal,
    CreateBoardModal,
    MasterBoardCharts,
    IconCut,
    IconFolderPlus,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      pageView: 'overview',
      loading: true,
      masterBoard: null,
      showEditModal: false,
      showCreateBoardModal: false,
      showEditBoardModal: false,
      selectedBoard: null,
      showPreviewModal: false,
      previewAttachment: null,
      previewUrl: null,
      previewLoading: false,
      previewError: null,
      masterBoardUploading: false,
      masterBoardFolderCreating: false,
      currentAttachmentFolderId: null,
      archiveSelectedItems: [],
      archiveSelectionAnchor: null,
      archiveMarquee: {
        active: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
      },
      archiveSuppressNextSelectionClear: false,
      macFolderIcon: require('../../assets/images/default/mac-folder.png'),
      inlineRenamingFolderId: null,
      inlineRenamingFolderName: '',
      inlineRenamingFolderSaving: false,
      inlineRenamingAttachmentId: null,
      inlineRenamingAttachmentName: '',
      inlineRenamingAttachmentSaving: false,
      archiveContextMenu: {
        visible: false,
        x: 0,
        y: 0,
        type: null,
        item: null,
      },
      archiveClipboard: {
        mode: null,
        items: [],
        sourceFolderId: null,
      },
      archivePasting: false,
    };
  },
  computed: {
    masterBoardData() {
      if (!this.masterBoard || this.masterBoard.error) return null;
      return this.masterBoard.data;
    },
    members() {
      const data = this.masterBoardData;
      if (!data || !data.members || !Array.isArray(data.members)) return [];
      return data.members;
    },
    attachments() {
      const data = this.masterBoardData;
      if (!data || !data.attachments || !Array.isArray(data.attachments)) return [];
      return data.attachments;
    },
    attachmentFolders() {
      const data = this.masterBoardData;
      if (!data || !data.attachment_folders || !Array.isArray(data.attachment_folders)) return [];
      return data.attachment_folders;
    },
    currentFolders() {
      const currentId = this.currentAttachmentFolderId ? String(this.currentAttachmentFolderId) : null;
      return this.attachmentFolders.filter(function (folder) {
        const parentId = folder.parent_folder_id ? String(folder.parent_folder_id) : null;
        return parentId === currentId;
      });
    },
    currentAttachments() {
      const currentId = this.currentAttachmentFolderId ? String(this.currentAttachmentFolderId) : null;
      return this.attachments.filter(function (att) {
        const folderId = att.folder_id ? String(att.folder_id) : null;
        return folderId === currentId;
      });
    },
    hasCurrentArchiveItems() {
      return this.currentFolders.length > 0 || this.currentAttachments.length > 0;
    },
    attachmentBreadcrumb() {
      const crumbs = [];
      let folderId = this.currentAttachmentFolderId;
      const folders = this.attachmentFolders;
      const maxDepth = 50;
      let depth = 0;
      while (folderId && depth < maxDepth) {
        const idStr = String(folderId);
        let found = null;
        for (let i = 0; i < folders.length; i++) {
          if (String(folders[i].id) === idStr) {
            found = folders[i];
            break;
          }
        }
        if (!found) break;
        crumbs.unshift({ id: found.id, name: found.name });
        folderId = found.parent_folder_id || null;
        depth++;
      }
      return crumbs;
    },
    subBoards() {
      const data = this.masterBoardData;
      if (!data || !data.sub_boards || !Array.isArray(data.sub_boards)) return [];
      return data.sub_boards;
    },
    canViewAllBoards() {
      const permissions = this.$store.getters.permissions || '';
      const permissionsArray = permissions.split(',');
      return permissionsArray.includes('view-all-task-management-board');
    },
    canEditAllBoards() {
      const permissions = this.$store.getters.permissions || '';
      const permissionsArray = permissions.split(',');
      return permissionsArray.includes('view-edit-task-management-board');
    },
    currentUserId() {
      const uid = this.$store.getters.userId;
      if (!uid) return null;
      return parseInt(uid);
    },
    canEditMasterBoard() {
      const data = this.masterBoardData;
      if (!data || parseInt(data.id) === 0) return false;
      const perms = this.$store.getters.permissions || '';
      if (perms.split(',').indexOf('view-edit-task-management-board') !== -1) return true;
      const uid = this.$store.getters.userId;
      if (!data.created_by || !data.created_by.id || !uid) return false;
      return parseInt(data.created_by.id) === parseInt(uid);
    },
    canUploadToMasterBoard() {
      if (!this.currentUserId || !this.masterBoardData || parseInt(this.masterBoardData.id) === 0) return false;
      if (this.canEditAllBoards) return true;
      const creatorId = this.masterBoardData.created_by && this.masterBoardData.created_by.id ? parseInt(this.masterBoardData.created_by.id) : null;
      if (creatorId === this.currentUserId) return true;
      return this.members.some(m => parseInt(m.id) === this.currentUserId);
    },
    previewIsImage() {
      const att = this.previewAttachment;
      if (!att) return false;
      const ft = (att.file_type || '').toLowerCase();
      if (ft.indexOf('image/') === 0) return true;
      const fn = att.original_filename || att.file_name || '';
      const ext = String(fn).split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].indexOf(ext) !== -1;
    },
    previewIsPdf() {
      const att = this.previewAttachment;
      if (!att) return false;
      const ft = (att.file_type || '').toLowerCase();
      if (ft === 'application/pdf') return true;
      const fn = att.original_filename || att.file_name || '';
      return String(fn).split('.').pop().toLowerCase() === 'pdf';
    },
    archivePastingLabel() {
      if (this.archivePasting) {
        const clip = this.archiveClipboard;
        if (clip && clip.mode === 'cut') return 'Moving...';
        return 'Pasting...';
      }
      if (this.masterBoardFolderCreating) return 'Creating folder...';
      return 'Uploading...';
    },
    archiveMarqueeStyle() {
      const marquee = this.archiveMarquee;
      if (!marquee.active) {
        return { display: 'none' };
      }
      const left = Math.min(marquee.startX, marquee.currentX);
      const top = Math.min(marquee.startY, marquee.currentY);
      const width = Math.abs(marquee.currentX - marquee.startX);
      const height = Math.abs(marquee.currentY - marquee.startY);
      return {
        left: left + 'px',
        top: top + 'px',
        width: width + 'px',
        height: height + 'px',
      };
    },
    archiveContextMenuActions() {
      const menu = this.archiveContextMenu;
      if (!menu.visible) return [];
      if (menu.type === 'background') {
        const actions = [];
        if (this.canUploadToMasterBoard) {
          actions.push({ id: 'new-folder', label: 'New Folder' });
        }
        if (this.canPasteArchiveClipboard()) {
          actions.push({ id: 'paste', label: 'Paste' });
        }
        return actions;
      }
      if (!menu.item) return [];
      const actions = [];
      const isMulti = this.archiveSelectedItems.length > 1;
      if (!isMulti) {
        actions.push({ id: 'open', label: 'Open' });
        if (menu.type === 'file' || this.isAttachmentFolderDownloadable(menu.item)) {
          actions.push({ id: 'download', label: 'Download' });
        }
      } else {
        const downloadCount = this.getDownloadableSelectedCount();
        if (downloadCount > 0) {
          actions.push({
            id: 'download',
            label: downloadCount > 1 ? 'Download (' + downloadCount + ')' : 'Download',
          });
        }
      }
      if (this.canUploadToMasterBoard) {
        actions.push({ id: 'cut', label: isMulti ? 'Cut (' + this.archiveSelectedItems.length + ')' : 'Cut' });
        actions.push({ id: 'copy', label: isMulti ? 'Copy (' + this.archiveSelectedItems.length + ')' : 'Copy' });
      }
      const deletableCount = this.getDeletableSelectedCount();
      if (deletableCount > 0) {
        actions.push({
          id: 'delete',
          label: deletableCount > 1 ? 'Delete (' + deletableCount + ')' : 'Delete',
          danger: true,
        });
      }
      if (!isMulti && menu.type === 'folder' && this.canEditAttachmentFolder(menu.item)) {
        actions.push({ id: 'rename', label: 'Rename' });
      } else if (!isMulti && menu.type === 'file' && this.canEditMasterBoardAttachment(menu.item)) {
        actions.push({ id: 'rename', label: 'Rename' });
      }
      return actions;
    },
  },
  apollo: {
    taskMasterBoard: {
      query: gql`
        query GetTaskMasterBoard($id: ID!) {
          taskMasterBoard(id: $id) {
            error
            error_code
            message
            data {
              id
              token
              name
              description
              color
              created_at
              created_by { id name }
              members { id token name email profile_picture_url }
              attachments {
                id
                folder_id
                file_name
                original_filename
                display_name
                file_type
                file_size
                uploaded_by { id name }
              }
              attachment_folders {
                id
                name
                parent_folder_id
                created_at
                created_by { id name }
              }
              sub_boards {
                id
                name
                description
                color
                master_board_id
                created_by { id name }
                members { id token name email }
                attachments {
                  id
                  file_name
                  original_filename
                  display_name
                  file_type
                  file_size
                }
                task_stats {
                  total
                  todo
                  in_progress
                  paused
                  in_review
                  done
                }
              }
            }
          }
        }
      `,
      variables() {
        return { id: String(this.id) };
      },
      skip() {
        return !this.id || parseInt(this.id) === 0;
      },
      result({ data, loading }) {
        this.loading = loading;
        this.masterBoard = data && data.taskMasterBoard ? data.taskMasterBoard : null;
      },
      fetchPolicy: 'cache-and-network',
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClickClearArchiveSelection);
    document.addEventListener('keydown', this.onDocumentKeydownArchive);
    document.addEventListener('scroll', this.closeArchiveContextMenu, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClickClearArchiveSelection);
    document.removeEventListener('keydown', this.onDocumentKeydownArchive);
    document.removeEventListener('scroll', this.closeArchiveContextMenu, true);
    this.teardownArchiveMarqueeListeners();
  },
  methods: {
    getTaskStats(board) {
      var defaults = { total: 0, todo: 0, in_progress: 0, paused: 0, in_review: 0, done: 0 };
      if (!board || !board.task_stats) {
        return defaults;
      }
      var s = board.task_stats;
      var statInt = function (v, fallback) {
        if (typeof v === 'number' && !isNaN(v)) {
          return v;
        }
        if (typeof v === 'string' && v !== '' && !isNaN(Number(v))) {
          return parseInt(v, 10);
        }
        return fallback;
      };
      return {
        total: statInt(s.total, defaults.total),
        todo: statInt(s.todo, defaults.todo),
        in_progress: statInt(s.in_progress, defaults.in_progress),
        paused: statInt(s.paused, defaults.paused),
        in_review: statInt(s.in_review, defaults.in_review),
        done: statInt(s.done, defaults.done),
      };
    },
    getProfilePicture(url) {
      if (!url) return '/img/default-avatar.png';
      if (url.indexOf('http') === 0) return url;
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    handleImageError(event) {
      event.target.src = '/img/default-avatar.png';
    },
    isCreator(member) {
      const data = this.masterBoardData;
      if (!data || !data.created_by || !member) return false;
      return parseInt(member.id) === parseInt(data.created_by.id);
    },
    goToProfile(member) {
      if (!member || !member.token) {
        return;
      }
      var resolved = this.$router.resolve({ path: '/profile/' + member.token });
      var url = resolved.href;
      if (!url || url.indexOf('http') !== 0) {
        var fp = resolved.fullPath || '';
        if (fp.indexOf('/') !== 0) {
          fp = '/' + fp;
        }
        url =
          window.location.origin +
          (window.location.pathname || '/') +
          (window.location.search || '') +
          '#' +
          fp;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    openBoard(boardId) {
      this.$router.push('/tasks/board/' + boardId);
    },
    canEditBoard(board) {
      if (this.canEditAllBoards) return true;
      if (!board || !board.created_by || !board.created_by.id || !this.currentUserId) return false;
      return parseInt(board.created_by.id) === this.currentUserId;
    },
    openCreateBoardModal() {
      this.showCreateBoardModal = true;
    },
    closeCreateBoardModal() {
      this.showCreateBoardModal = false;
    },
    handleBoardCreated() {
      this.closeCreateBoardModal();
      if (this.$apollo.queries.taskMasterBoard) {
        this.$apollo.queries.taskMasterBoard.refetch();
      }
    },
    openEditBoard(board) {
      this.selectedBoard = board;
      this.showEditBoardModal = true;
    },
    closeEditBoard() {
      this.showEditBoardModal = false;
      this.selectedBoard = null;
    },
    handleBoardUpdated() {
      this.closeEditBoard();
      if (this.$apollo.queries.taskMasterBoard) {
        this.$apollo.queries.taskMasterBoard.refetch();
      }
    },
    openEditModal() {
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    handleMasterBoardUpdated() {
      this.closeEditModal();
      if (this.$apollo.queries.taskMasterBoard) {
        this.$apollo.queries.taskMasterBoard.refetch();
      }
    },
    async viewAttachment(attachment) {
      this.cancelInlineRenameAttachment();
      this.cancelInlineRenameAttachmentFolder();

      this.previewAttachment = attachment;
      this.previewLoading = true;
      this.previewError = null;
      this.previewUrl = null;
      this.showPreviewModal = true;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskMasterBoardAttachmentContent($id: ID!) {
              getTaskMasterBoardAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  attachment { id original_filename }
                }
              }
            }
          `,
          variables: { id: attachment.id },
        });

        const payload = data && data.getTaskMasterBoardAttachmentContent;
        if (payload && payload.error) {
          this.previewError = payload.message || 'Failed to load preview.';
          return;
        }

        const contentData = payload && payload.data;
        if (!contentData || !contentData.content) {
          this.previewError = 'Failed to load preview.';
          return;
        }

        const base64Content = contentData.content;
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const mimeType = contentData.file_type || 'application/octet-stream';
        const blob = new Blob([bytes], { type: mimeType });
        this.previewUrl = URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error loading attachment for preview:', error);
        this.previewError = 'Failed to load preview. Please try again.';
      } finally {
        this.previewLoading = false;
      }
    },
    closePreviewModal() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.showPreviewModal = false;
      this.previewAttachment = null;
      this.previewUrl = null;
      this.previewError = null;
    },
    saveDownloadFromBase64(base64Content, fileType, filename) {
      const binaryString = atob(base64Content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: fileType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async downloadAttachment(attachment) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskMasterBoardAttachmentContent($id: ID!) {
              getTaskMasterBoardAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  attachment { id original_filename }
                }
              }
            }
          `,
          variables: { id: attachment.id },
        });

        const payload = data && data.getTaskMasterBoardAttachmentContent;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to download attachment.');
          return;
        }

        const contentData = payload && payload.data;
        if (!contentData || !contentData.content) {
          alert('Failed to download attachment.');
          return;
        }

        const downloadName = (contentData.attachment && contentData.attachment.original_filename)
          || attachment.original_filename
          || 'download';
        this.saveDownloadFromBase64(contentData.content, contentData.file_type, downloadName);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        alert('Failed to download attachment. Please try again.');
      }
    },
    async downloadAttachmentFolder(folder) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskMasterBoardAttachmentFolderContent($id: ID!) {
              getTaskMasterBoardAttachmentFolderContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  download_filename
                  folder { id name }
                }
              }
            }
          `,
          variables: { id: folder.id },
        });

        const payload = data && data.getTaskMasterBoardAttachmentFolderContent;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to download folder.');
          return;
        }

        const contentData = payload && payload.data;
        if (!contentData || !contentData.content) {
          alert('Failed to download folder.');
          return;
        }

        const downloadName = contentData.download_filename
          || ((contentData.folder && contentData.folder.name) ? contentData.folder.name + '.zip' : null)
          || (folder.name ? folder.name + '.zip' : 'folder.zip');
        this.saveDownloadFromBase64(contentData.content, contentData.file_type || 'application/zip', downloadName);
      } catch (error) {
        console.error('Error downloading folder:', error);
        alert('Failed to download folder. Please try again.');
      }
    },
    canEditMasterBoardAttachment(att) {
      if (!att || !this.currentUserId) return false;
      const uploaderId = att.uploaded_by && att.uploaded_by.id ? parseInt(att.uploaded_by.id) : null;
      return uploaderId === this.currentUserId;
    },
    canEditAttachmentFolder(folder) {
      if (!folder || !this.currentUserId) return false;
      if (this.canEditAllBoards) return true;
      const creatorId = folder.created_by && folder.created_by.id ? parseInt(folder.created_by.id) : null;
      return creatorId === this.currentUserId;
    },
    openAttachmentFolder(folder) {
      if (!folder || !folder.id) return;
      this.currentAttachmentFolderId = folder.id;
      this.clearArchiveSelection();
      this.cancelInlineRenameAttachmentFolder();
      this.cancelInlineRenameAttachment();
    },
    navigateAttachmentFolder(folderId) {
      this.currentAttachmentFolderId = folderId;
      this.clearArchiveSelection();
      this.cancelInlineRenameAttachmentFolder();
      this.cancelInlineRenameAttachment();
    },
    clearArchiveSelection() {
      this.archiveSelectedItems = [];
      this.archiveSelectionAnchor = null;
    },
    getArchiveSelectionKey(type, id) {
      return type + ':' + String(id);
    },
    setArchiveSelection(items, anchor) {
      const normalized = [];
      const seen = {};
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item || !item.type || !item.id) continue;
        const key = this.getArchiveSelectionKey(item.type, item.id);
        if (seen[key]) continue;
        seen[key] = true;
        normalized.push({ type: item.type, id: String(item.id) });
      }
      this.archiveSelectedItems = normalized;
      if (anchor && anchor.type && anchor.id) {
        this.archiveSelectionAnchor = { type: anchor.type, id: String(anchor.id) };
      } else if (normalized.length > 0) {
        this.archiveSelectionAnchor = { type: normalized[0].type, id: normalized[0].id };
      } else {
        this.archiveSelectionAnchor = null;
      }
    },
    isArchiveItemSelected(type, id) {
      const key = this.getArchiveSelectionKey(type, id);
      const items = this.archiveSelectedItems;
      for (let i = 0; i < items.length; i++) {
        if (this.getArchiveSelectionKey(items[i].type, items[i].id) === key) {
          return true;
        }
      }
      return false;
    },
    getArchiveDisplayOrderItems() {
      const items = [];
      const folders = this.currentFolders;
      const files = this.currentAttachments;
      for (let i = 0; i < folders.length; i++) {
        items.push({ type: 'folder', id: String(folders[i].id) });
      }
      for (let j = 0; j < files.length; j++) {
        items.push({ type: 'file', id: String(files[j].id) });
      }
      return items;
    },
    handleArchiveItemSelection(type, id, event) {
      const idStr = String(id);
      const isMod = event && (event.ctrlKey || event.metaKey);
      const isShift = event && event.shiftKey;
      if (isShift) {
        this.selectArchiveItemRange(type, idStr);
        return;
      }
      if (isMod) {
        this.toggleArchiveItemSelection(type, idStr);
        return;
      }
      this.setArchiveSelection([{ type: type, id: idStr }], { type: type, id: idStr });
    },
    toggleArchiveItemSelection(type, id) {
      const idStr = String(id);
      const key = this.getArchiveSelectionKey(type, idStr);
      const items = this.archiveSelectedItems.slice();
      let foundIndex = -1;
      for (let i = 0; i < items.length; i++) {
        if (this.getArchiveSelectionKey(items[i].type, items[i].id) === key) {
          foundIndex = i;
          break;
        }
      }
      if (foundIndex !== -1) {
        items.splice(foundIndex, 1);
        this.archiveSelectedItems = items;
        if (items.length === 0) {
          this.archiveSelectionAnchor = null;
        }
        return;
      }
      items.push({ type: type, id: idStr });
      this.archiveSelectedItems = items;
      this.archiveSelectionAnchor = { type: type, id: idStr };
    },
    selectArchiveItemRange(type, id) {
      const idStr = String(id);
      const anchor = this.archiveSelectionAnchor;
      if (!anchor) {
        this.setArchiveSelection([{ type: type, id: idStr }], { type: type, id: idStr });
        return;
      }
      const ordered = this.getArchiveDisplayOrderItems();
      const targetKey = this.getArchiveSelectionKey(type, idStr);
      const anchorKey = this.getArchiveSelectionKey(anchor.type, anchor.id);
      let anchorIndex = -1;
      let targetIndex = -1;
      for (let i = 0; i < ordered.length; i++) {
        const itemKey = this.getArchiveSelectionKey(ordered[i].type, ordered[i].id);
        if (itemKey === anchorKey) anchorIndex = i;
        if (itemKey === targetKey) targetIndex = i;
      }
      if (anchorIndex === -1 || targetIndex === -1) {
        this.setArchiveSelection([{ type: type, id: idStr }], { type: type, id: idStr });
        return;
      }
      const start = Math.min(anchorIndex, targetIndex);
      const end = Math.max(anchorIndex, targetIndex);
      this.setArchiveSelection(ordered.slice(start, end + 1), anchor);
    },
    prepareArchiveContextMenuSelection(type, id) {
      if (!this.isArchiveItemSelected(type, id)) {
        this.setArchiveSelection([{ type: type, id: String(id) }], { type: type, id: String(id) });
      }
    },
    getArchiveItemRecord(type, id) {
      const idStr = String(id);
      if (type === 'folder') {
        const folders = this.attachmentFolders;
        for (let i = 0; i < folders.length; i++) {
          if (String(folders[i].id) === idStr) return folders[i];
        }
        return null;
      }
      if (type === 'file') {
        const attachments = this.attachments;
        for (let j = 0; j < attachments.length; j++) {
          if (String(attachments[j].id) === idStr) return attachments[j];
        }
        return null;
      }
      return null;
    },
    getSelectedArchiveItemsWithMeta() {
      const result = [];
      const items = this.archiveSelectedItems;
      for (let i = 0; i < items.length; i++) {
        const selected = items[i];
        const record = this.getArchiveItemRecord(selected.type, selected.id);
        if (!record) continue;
        const name = selected.type === 'folder'
          ? (record.name || '')
          : this.getAttachmentDisplayName(record);
        result.push({
          type: selected.type,
          id: selected.id,
          name: name,
        });
      }
      return result;
    },
    onArchiveGridMouseDown(event) {
      if (!event || event.button !== 0) return;
      if (event.target && event.target.closest('.archive-grid-item')) return;
      if (event.target && event.target.closest('.archive-item-kebab-menu')) return;
      this.clearArchiveSelection();
      event.preventDefault();
      this.archiveMarquee.active = true;
      this.archiveMarquee.startX = event.clientX;
      this.archiveMarquee.startY = event.clientY;
      this.archiveMarquee.currentX = event.clientX;
      this.archiveMarquee.currentY = event.clientY;
      document.addEventListener('mousemove', this.onArchiveMarqueeMouseMove);
      document.addEventListener('mouseup', this.onArchiveMarqueeMouseUp);
    },
    onArchiveMarqueeMouseMove(event) {
      if (!this.archiveMarquee.active || !event) return;
      this.archiveMarquee.currentX = event.clientX;
      this.archiveMarquee.currentY = event.clientY;
      const selected = this.getArchiveMarqueeSelectedItems();
      if (selected.length > 0) {
        this.setArchiveSelection(selected, selected[0]);
      } else {
        this.archiveSelectedItems = [];
        this.archiveSelectionAnchor = null;
      }
    },
    onArchiveMarqueeMouseUp() {
      if (!this.archiveMarquee.active) return;
      const marqueeRect = this.getArchiveMarqueeRect();
      const hadMarqueeDrag = marqueeRect.width >= 4 || marqueeRect.height >= 4;
      this.commitArchiveMarqueeSelection();
      this.teardownArchiveMarqueeListeners();
      this.archiveMarquee.active = false;
      if (hadMarqueeDrag) {
        this.archiveSuppressNextSelectionClear = true;
      }
    },
    teardownArchiveMarqueeListeners() {
      document.removeEventListener('mousemove', this.onArchiveMarqueeMouseMove);
      document.removeEventListener('mouseup', this.onArchiveMarqueeMouseUp);
    },
    getArchiveMarqueeRect() {
      const marquee = this.archiveMarquee;
      const left = Math.min(marquee.startX, marquee.currentX);
      const top = Math.min(marquee.startY, marquee.currentY);
      const right = Math.max(marquee.startX, marquee.currentX);
      const bottom = Math.max(marquee.startY, marquee.currentY);
      return {
        left: left,
        top: top,
        right: right,
        bottom: bottom,
        width: right - left,
        height: bottom - top,
      };
    },
    archiveRectsIntersect(rectA, rectB) {
      return !(
        rectA.right < rectB.left ||
        rectA.left > rectB.right ||
        rectA.bottom < rectB.top ||
        rectA.top > rectB.bottom
      );
    },
    commitArchiveMarqueeSelection() {
      const selected = this.getArchiveMarqueeSelectedItems();
      if (selected.length > 0) {
        this.setArchiveSelection(selected, selected[0]);
      }
    },
    getArchiveMarqueeSelectedItems() {
      const marqueeRect = this.getArchiveMarqueeRect();
      if (marqueeRect.width < 4 && marqueeRect.height < 4) return [];
      const grid = this.$refs.archiveGrid;
      if (!grid || !grid.querySelectorAll) return [];
      const itemEls = grid.querySelectorAll('.archive-grid-item');
      const selected = [];
      for (let i = 0; i < itemEls.length; i++) {
        const el = itemEls[i];
        const itemRect = el.getBoundingClientRect();
        const normalizedItemRect = {
          left: itemRect.left,
          top: itemRect.top,
          right: itemRect.right,
          bottom: itemRect.bottom,
        };
        if (this.archiveRectsIntersect(marqueeRect, normalizedItemRect)) {
          const type = el.getAttribute('data-archive-type');
          const id = el.getAttribute('data-archive-id');
          if (type && id) {
            selected.push({ type: type, id: String(id) });
          }
        }
      }
      return selected;
    },
    onDocumentClickClearArchiveSelection(event) {
      if (this.archiveSuppressNextSelectionClear) {
        this.archiveSuppressNextSelectionClear = false;
        return;
      }
      if (event && event.target && event.target.closest('.archive-context-menu')) return;
      if (event && event.target && event.target.closest('.archive-item-kebab-menu')) return;
      if (this.archiveContextMenu.visible) {
        this.closeArchiveContextMenu();
        return;
      }
      if (!this.archiveSelectedItems.length) return;
      if (event && event.target && event.target.closest('.archive-grid-item')) return;
      this.clearArchiveSelection();
    },
    onDocumentKeydownArchive(event) {
      if (!event) return;
      if (event.key === 'Escape') {
        if (this.archiveContextMenu.visible) {
          this.closeArchiveContextMenu();
        } else if (this.archiveClipboard && this.archiveClipboard.items && this.archiveClipboard.items.length) {
          this.clearArchiveClipboard();
        } else if (this.archiveSelectedItems.length) {
          this.clearArchiveSelection();
        }
        return;
      }
      const tag = event.target && event.target.tagName ? event.target.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea') return;
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (this.archiveSelectedItems.length && this.canDeleteArchiveSelection()) {
          event.preventDefault();
          this.deleteArchiveSelection();
        }
        return;
      }
      const isMod = event.ctrlKey || event.metaKey;
      if (!isMod) return;
      if (event.key === 'a' || event.key === 'A') {
        if (this.hasCurrentArchiveItems) {
          event.preventDefault();
          const ordered = this.getArchiveDisplayOrderItems();
          if (ordered.length > 0) {
            this.setArchiveSelection(ordered, ordered[0]);
          }
        }
        return;
      }
      if (event.key === 'c' || event.key === 'C') {
        if (this.archiveSelectedItems.length && this.canUploadToMasterBoard) {
          event.preventDefault();
          this.copyArchiveSelection();
        }
        return;
      }
      if (event.key === 'x' || event.key === 'X') {
        if (this.archiveSelectedItems.length && this.canUploadToMasterBoard) {
          event.preventDefault();
          this.cutArchiveSelection();
        }
        return;
      }
      if (event.key === 'v' || event.key === 'V') {
        if (this.canPasteArchiveClipboard()) {
          event.preventDefault();
          this.pasteArchiveClipboard();
        }
      }
    },
    openArchiveContextMenu(event, type, item) {
      if (!item || !item.id) return;
      let clientX = 0;
      let clientY = 0;
      if (event && typeof event.clientX === 'number') {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      this.openArchiveContextMenuAt(clientX, clientY, type, item);
    },
    openArchiveContextMenuAt(x, y, type, item) {
      if (!item || !item.id) return;
      this.prepareArchiveContextMenuSelection(type, item.id);
      const menuWidth = 190;
      const menuHeight = type === 'file' ? 260 : 220;
      const maxX = window.innerWidth - menuWidth - 8;
      const maxY = window.innerHeight - menuHeight - 8;
      const posX = Math.max(8, Math.min(x, maxX));
      const posY = Math.max(8, Math.min(y, maxY));
      this.archiveContextMenu = {
        visible: true,
        x: posX,
        y: posY,
        type: type,
        item: item,
      };
    },
    openArchiveItemKebabMenu(event, type, item) {
      if (!item || !item.id || !event) return;
      this.prepareArchiveContextMenuSelection(type, item.id);
      if (this.isArchiveContextMenuOpenForItem(type, item.id)) {
        this.closeArchiveContextMenu();
        return;
      }
      let clientX = event.clientX || 0;
      let clientY = event.clientY || 0;
      if (event.currentTarget && event.currentTarget.getBoundingClientRect) {
        const rect = event.currentTarget.getBoundingClientRect();
        clientX = rect.left;
        clientY = rect.bottom + 4;
      }
      this.openArchiveContextMenuAt(clientX, clientY, type, item);
    },
    isArchiveContextMenuOpenForItem(type, id) {
      const menu = this.archiveContextMenu;
      if (!menu.visible || !menu.item || !menu.item.id) return false;
      return menu.type === type && String(menu.item.id) === String(id);
    },
    openArchiveBackgroundContextMenu(event) {
      if (!this.canUploadToMasterBoard && !this.canPasteArchiveClipboard()) return;
      let clientX = 0;
      let clientY = 0;
      if (event && typeof event.clientX === 'number') {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      this.clearArchiveSelection();
      const menuWidth = 190;
      const menuHeight = (this.canUploadToMasterBoard && this.canPasteArchiveClipboard()) ? 88 : 48;
      const maxX = window.innerWidth - menuWidth - 8;
      const maxY = window.innerHeight - menuHeight - 8;
      const posX = Math.max(8, Math.min(clientX, maxX));
      const posY = Math.max(8, Math.min(clientY, maxY));
      this.archiveContextMenu = {
        visible: true,
        x: posX,
        y: posY,
        type: 'background',
        item: null,
      };
    },
    openArchiveExplorerContextMenu(event) {
      if (event && event.target && event.target.closest('.archive-grid-item')) return;
      this.openArchiveBackgroundContextMenu(event);
    },
    closeArchiveContextMenu() {
      this.archiveContextMenu.visible = false;
      this.archiveContextMenu.type = null;
      this.archiveContextMenu.item = null;
    },
    executeArchiveContextMenuAction(actionId) {
      const menu = this.archiveContextMenu;
      if (!menu.visible) return;
      if (menu.type === 'background') {
        this.closeArchiveContextMenu();
        if (actionId === 'new-folder') {
          this.createAttachmentFolder();
        } else if (actionId === 'paste') {
          this.pasteArchiveClipboard();
        }
        return;
      }
      if (!menu.item) return;
      const item = menu.item;
      const type = menu.type;
      this.closeArchiveContextMenu();
      if (actionId === 'open') {
        if (type === 'folder') {
          this.openAttachmentFolder(item);
        } else {
          this.viewAttachment(item);
        }
        return;
      }
      if (actionId === 'download') {
        if (this.archiveSelectedItems.length > 1) {
          this.downloadArchiveSelection();
        } else if (type === 'folder') {
          this.downloadAttachmentFolder(item);
        } else if (type === 'file') {
          this.downloadAttachment(item);
        }
        return;
      }
      if (actionId === 'copy') {
        this.copyArchiveSelection();
        return;
      }
      if (actionId === 'cut') {
        this.cutArchiveSelection();
        return;
      }
      if (actionId === 'rename') {
        if (type === 'folder') {
          this.startInlineRenameAttachmentFolder(item);
        } else {
          this.startInlineRenameAttachment(item);
        }
        return;
      }
      if (actionId === 'delete') {
        this.deleteArchiveSelection();
      }
    },
    getSelectedItemsEligibleForDelete() {
      const selected = this.archiveSelectedItems;
      const folders = [];
      const files = [];
      for (let i = 0; i < selected.length; i++) {
        const sel = selected[i];
        const record = this.getArchiveItemRecord(sel.type, sel.id);
        if (!record) continue;
        if (sel.type === 'folder' && this.canEditAttachmentFolder(record)) {
          folders.push(record);
        } else if (sel.type === 'file' && this.canEditMasterBoardAttachment(record)) {
          files.push(record);
        }
      }
      const topLevelFolders = [];
      for (let j = 0; j < folders.length; j++) {
        const folder = folders[j];
        let isNestedInSelection = false;
        for (let k = 0; k < folders.length; k++) {
          if (String(folders[k].id) === String(folder.id)) continue;
          if (this.isFolderInsideTree(folder.id, folders[k].id)) {
            isNestedInSelection = true;
            break;
          }
        }
        if (!isNestedInSelection) {
          topLevelFolders.push(folder);
        }
      }
      const deletingFolderIds = {};
      for (let m = 0; m < topLevelFolders.length; m++) {
        deletingFolderIds[String(topLevelFolders[m].id)] = true;
      }
      const filesToDelete = [];
      for (let n = 0; n < files.length; n++) {
        const file = files[n];
        const parentId = file.folder_id ? String(file.folder_id) : null;
        if (parentId && deletingFolderIds[parentId]) continue;
        filesToDelete.push(file);
      }
      return {
        folders: topLevelFolders,
        files: filesToDelete,
      };
    },
    getDeletableSelectedCount() {
      const eligible = this.getSelectedItemsEligibleForDelete();
      return eligible.folders.length + eligible.files.length;
    },
    getSelectedItemsForDownload() {
      const selected = this.archiveSelectedItems;
      const folders = [];
      const files = [];
      for (let i = 0; i < selected.length; i++) {
        const sel = selected[i];
        const record = this.getArchiveItemRecord(sel.type, sel.id);
        if (!record) continue;
        if (sel.type === 'folder') {
          if (this.isAttachmentFolderDownloadable(record)) {
            folders.push(record);
          }
        } else if (sel.type === 'file') {
          files.push(record);
        }
      }
      const topLevelFolders = [];
      for (let j = 0; j < folders.length; j++) {
        const folder = folders[j];
        let isNestedInSelection = false;
        for (let k = 0; k < folders.length; k++) {
          if (String(folders[k].id) === String(folder.id)) continue;
          if (this.isFolderInsideTree(folder.id, folders[k].id)) {
            isNestedInSelection = true;
            break;
          }
        }
        if (!isNestedInSelection) {
          topLevelFolders.push(folder);
        }
      }
      const downloadingFolderIds = {};
      for (let m = 0; m < topLevelFolders.length; m++) {
        downloadingFolderIds[String(topLevelFolders[m].id)] = true;
      }
      const filesToDownload = [];
      for (let n = 0; n < files.length; n++) {
        const file = files[n];
        const parentId = file.folder_id ? String(file.folder_id) : null;
        if (parentId && downloadingFolderIds[parentId]) continue;
        filesToDownload.push(file);
      }
      return {
        folders: topLevelFolders,
        files: filesToDownload,
      };
    },
    getDownloadableSelectedCount() {
      const items = this.getSelectedItemsForDownload();
      return items.folders.length + items.files.length;
    },
    async downloadArchiveSelection() {
      const items = this.getSelectedItemsForDownload();
      const folders = items.folders;
      const files = items.files;
      const total = folders.length + files.length;
      if (total === 0) return;

      for (let i = 0; i < folders.length; i++) {
        await this.downloadAttachmentFolder(folders[i]);
        if (i < folders.length - 1 || files.length > 0) {
          await new Promise(function(resolve) { setTimeout(resolve, 300); });
        }
      }
      for (let j = 0; j < files.length; j++) {
        await this.downloadAttachment(files[j]);
        if (j < files.length - 1) {
          await new Promise(function(resolve) { setTimeout(resolve, 300); });
        }
      }
    },
    canDeleteArchiveSelection() {
      return this.getDeletableSelectedCount() > 0;
    },
    async deleteArchiveSelection() {
      const eligible = this.getSelectedItemsEligibleForDelete();
      const folders = eligible.folders;
      const files = eligible.files;
      const total = folders.length + files.length;
      if (total === 0) return;

      let confirmText = '';
      if (total === 1) {
        if (folders.length === 1) {
          const folderName = folders[0].name || 'this folder';
          confirmText = 'You are about to delete "' + folderName + '" and all files and subfolders inside it. This cannot be undone.';
        } else {
          const file = files[0];
          const fileName = file.display_name || file.original_filename || file.file_name || 'this file';
          confirmText = 'You are about to delete "' + fileName + '". This cannot be undone.';
        }
      } else {
        const parts = [];
        if (folders.length > 0) {
          parts.push(folders.length + ' ' + (folders.length === 1 ? 'folder' : 'folders'));
        }
        if (files.length > 0) {
          parts.push(files.length + ' ' + (files.length === 1 ? 'file' : 'files'));
        }
        confirmText = 'You are about to delete ' + parts.join(' and ') + '. This cannot be undone.';
        if (folders.length > 0) {
          confirmText += ' Selected folders and all their contents will be removed.';
        }
      }

      const result = await this.$swal.fire({
        title: total === 1 ? 'Delete item?' : 'Delete ' + total + ' items?',
        text: confirmText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: total === 1 ? 'Yes, delete it' : 'Yes, delete them',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;

      let hadError = false;
      let errorMessage = '';
      try {
        for (let i = 0; i < folders.length; i++) {
          const folder = folders[i];
          const { data } = await this.$apollo.mutate({
            mutation: gql`
              mutation DeleteTaskMasterBoardAttachmentFolder($id: ID!) {
                deleteTaskMasterBoardAttachmentFolder(id: $id) {
                  error
                  error_code
                  message
                }
              }
            `,
            variables: { id: folder.id },
          });
          const payload = data && data.deleteTaskMasterBoardAttachmentFolder;
          if (payload && payload.error) {
            hadError = true;
            errorMessage = payload.message || 'Failed to delete folder.';
            break;
          }
          if (this.currentAttachmentFolderId && this.isFolderInsideTree(this.currentAttachmentFolderId, folder.id)) {
            this.navigateAttachmentFolder(folder.parent_folder_id || null);
          }
        }
        if (!hadError) {
          for (let j = 0; j < files.length; j++) {
            const att = files[j];
            const { data } = await this.$apollo.mutate({
              mutation: gql`
                mutation DeleteTaskMasterBoardAttachment($id: ID!) {
                  deleteTaskMasterBoardAttachment(id: $id) {
                    error
                    error_code
                    message
                  }
                }
              `,
              variables: { id: att.id },
            });
            const payload = data && data.deleteTaskMasterBoardAttachment;
            if (payload && payload.error) {
              hadError = true;
              errorMessage = payload.message || 'Failed to delete attachment.';
              break;
            }
          }
        }
        if (hadError) {
          alert(errorMessage);
        } else {
          this.cancelInlineRenameAttachmentFolder();
          this.cancelInlineRenameAttachment();
          this.clearArchiveSelection();
          if (this.$apollo.queries.taskMasterBoard) {
            this.$apollo.queries.taskMasterBoard.refetch();
          }
        }
      } catch (error) {
        console.error('Error deleting archive items:', error);
        alert('Failed to delete. Please try again.');
      }
    },
    getArchiveItemSourceFolderId(type, item) {
      if (!item) return null;
      if (type === 'folder') {
        return item.parent_folder_id || null;
      }
      if (type === 'file') {
        return item.folder_id || null;
      }
      return null;
    },
    setArchiveClipboard(mode) {
      if (!this.canUploadToMasterBoard) return;
      const items = this.getSelectedArchiveItemsWithMeta();
      if (!items.length) return;
      this.archiveClipboard = {
        mode: mode,
        items: items,
        sourceFolderId: this.currentAttachmentFolderId || null,
      };
    },
    copyArchiveSelection() {
      this.setArchiveClipboard('copy');
    },
    cutArchiveSelection() {
      this.setArchiveClipboard('cut');
    },
    clearArchiveClipboard() {
      this.archiveClipboard = {
        mode: null,
        items: [],
        sourceFolderId: null,
      };
    },
    isArchiveItemCut(type, id) {
      const clip = this.archiveClipboard;
      if (!clip || clip.mode !== 'cut' || !clip.items || !clip.items.length) return false;
      const key = this.getArchiveSelectionKey(type, id);
      for (let i = 0; i < clip.items.length; i++) {
        if (this.getArchiveSelectionKey(clip.items[i].type, clip.items[i].id) === key) {
          return true;
        }
      }
      return false;
    },
    isSameArchiveFolderId(folderIdA, folderIdB) {
      const a = folderIdA ? String(folderIdA) : null;
      const b = folderIdB ? String(folderIdB) : null;
      return a === b;
    },
    canPasteArchiveClipboard() {
      const clip = this.archiveClipboard;
      if (!this.canUploadToMasterBoard || !clip.items || !clip.items.length) return false;
      const destinationFolderId = this.currentAttachmentFolderId || null;
      if (clip.mode === 'cut') {
        let allSameLocation = true;
        for (let i = 0; i < clip.items.length; i++) {
          const item = clip.items[i];
          const record = this.getArchiveItemRecord(item.type, item.id);
          if (!record) continue;
          const itemParent = this.getArchiveItemSourceFolderId(item.type, record);
          if (!this.isSameArchiveFolderId(itemParent, destinationFolderId)) {
            allSameLocation = false;
            break;
          }
        }
        if (allSameLocation) return false;
      }
      for (let j = 0; j < clip.items.length; j++) {
        const folderItem = clip.items[j];
        if (folderItem.type === 'folder') {
          const clipId = folderItem.id;
          if (this.currentAttachmentFolderId && String(this.currentAttachmentFolderId) === String(clipId)) {
            return false;
          }
          if (this.currentAttachmentFolderId && this.isFolderInsideTree(this.currentAttachmentFolderId, clipId)) {
            return false;
          }
        }
      }
      return true;
    },
    async pasteArchiveClipboard() {
      if (!this.canPasteArchiveClipboard() || this.archivePasting) return;
      const clip = this.archiveClipboard;
      const destinationFolderId = this.currentAttachmentFolderId;
      const isCut = clip.mode === 'cut';
      const items = clip.items.slice();
      const folders = [];
      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type === 'folder') {
          folders.push(items[i]);
        } else if (items[i].type === 'file') {
          files.push(items[i]);
        }
      }
      const orderedItems = folders.concat(files);
      this.archivePasting = true;
      let hadError = false;
      let errorMessage = '';
      try {
        for (let j = 0; j < orderedItems.length; j++) {
          const item = orderedItems[j];
          if (item.type === 'file') {
            const mutation = isCut
              ? gql`
                mutation MoveTaskMasterBoardAttachment($id: ID!, $destination_folder_id: ID) {
                  moveTaskMasterBoardAttachment(id: $id, destination_folder_id: $destination_folder_id) {
                    error
                    error_code
                    message
                  }
                }
              `
              : gql`
                mutation CopyTaskMasterBoardAttachment($id: ID!, $destination_folder_id: ID) {
                  copyTaskMasterBoardAttachment(id: $id, destination_folder_id: $destination_folder_id) {
                    error
                    error_code
                    message
                  }
                }
              `;
            const { data } = await this.$apollo.mutate({
              mutation: mutation,
              variables: {
                id: item.id,
                destination_folder_id: destinationFolderId,
              },
            });
            const payloadKey = isCut ? 'moveTaskMasterBoardAttachment' : 'copyTaskMasterBoardAttachment';
            const payload = data && data[payloadKey];
            if (payload && payload.error) {
              hadError = true;
              errorMessage = payload.message || (isCut ? 'Failed to move file.' : 'Failed to paste file.');
              break;
            }
          } else if (item.type === 'folder') {
            const mutation = isCut
              ? gql`
                mutation MoveTaskMasterBoardAttachmentFolder($id: ID!, $destination_folder_id: ID) {
                  moveTaskMasterBoardAttachmentFolder(id: $id, destination_folder_id: $destination_folder_id) {
                    error
                    error_code
                    message
                  }
                }
              `
              : gql`
                mutation CopyTaskMasterBoardAttachmentFolder($id: ID!, $destination_folder_id: ID) {
                  copyTaskMasterBoardAttachmentFolder(id: $id, destination_folder_id: $destination_folder_id) {
                    error
                    error_code
                    message
                  }
                }
              `;
            const { data } = await this.$apollo.mutate({
              mutation: mutation,
              variables: {
                id: item.id,
                destination_folder_id: destinationFolderId,
              },
            });
            const payloadKey = isCut ? 'moveTaskMasterBoardAttachmentFolder' : 'copyTaskMasterBoardAttachmentFolder';
            const payload = data && data[payloadKey];
            if (payload && payload.error) {
              hadError = true;
              errorMessage = payload.message || (isCut ? 'Failed to move folder.' : 'Failed to paste folder.');
              break;
            }
          }
        }
        if (hadError) {
          alert(errorMessage);
        } else {
          if (isCut) {
            this.clearArchiveClipboard();
            this.clearArchiveSelection();
          }
          if (this.$apollo.queries.taskMasterBoard) {
            this.$apollo.queries.taskMasterBoard.refetch();
          }
        }
      } catch (error) {
        console.error('Error pasting archive items:', error);
        alert(isCut ? 'Failed to move. Please try again.' : 'Failed to paste. Please try again.');
      } finally {
        this.archivePasting = false;
      }
    },
    onArchiveLabelClick(event, type, item) {
      if (!item || !item.id) return;
      const wasOnlySelected = this.archiveSelectedItems.length === 1 && this.isArchiveItemSelected(type, item.id);
      this.handleArchiveItemSelection(type, item.id, event);
      if (!wasOnlySelected) return;
      if (type === 'folder') {
        this.startInlineRenameAttachmentFolder(item);
      } else if (type === 'file') {
        this.startInlineRenameAttachment(item);
      }
    },
    onArchiveGridItemClick(event, type, id) {
      this.handleArchiveItemSelection(type, id, event);
    },
    getAttachmentDisplayName(att) {
      if (!att) return '';
      return att.display_name || att.original_filename || att.file_name || '';
    },
    getAttachmentOriginalName(att) {
      if (!att) return '';
      return att.original_filename || att.file_name || '';
    },
    getFolderContentsLabel(folderId) {
      const counts = this.getFolderDirectContents(folderId);
      const parts = [];
      if (counts.subfolders > 0) {
        parts.push(counts.subfolders + ' ' + (counts.subfolders === 1 ? 'folder' : 'folders'));
      }
      if (counts.files > 0) {
        parts.push(counts.files + ' ' + (counts.files === 1 ? 'file' : 'files'));
      }
      if (parts.length === 0) return 'Empty';
      return parts.join(', ');
    },
    getFolderDirectContents(folderId) {
      const idStr = String(folderId);
      let subfolders = 0;
      let files = 0;
      const folders = this.attachmentFolders;
      for (let i = 0; i < folders.length; i++) {
        const folder = folders[i];
        const parentId = folder.parent_folder_id ? String(folder.parent_folder_id) : null;
        if (parentId === idStr) subfolders++;
      }
      const attachments = this.attachments;
      for (let j = 0; j < attachments.length; j++) {
        const att = attachments[j];
        const attFolderId = att.folder_id ? String(att.folder_id) : null;
        if (attFolderId === idStr) files++;
      }
      return { subfolders: subfolders, files: files };
    },
    formatArchiveFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    formatArchiveDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatArchiveMimeType(mimeType) {
      const ft = mimeType ? String(mimeType).toLowerCase() : '';
      if (!ft) return 'Unknown';
      if (ft === 'application/pdf') return 'PDF Document';
      if (ft === 'application/msword') return 'Word Document';
      if (ft === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'Word Document';
      if (ft === 'application/vnd.ms-excel') return 'Excel Spreadsheet';
      if (ft === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'Excel Spreadsheet';
      if (ft === 'application/vnd.ms-powerpoint') return 'PowerPoint Presentation';
      if (ft === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return 'PowerPoint Presentation';
      if (ft === 'text/plain') return 'Plain Text';
      if (ft === 'text/csv' || ft === 'application/csv') return 'CSV File';
      if (ft.indexOf('image/') === 0) {
        const subtype = ft.split('/').pop();
        if (subtype) return subtype.toUpperCase() + ' Image';
        return 'Image';
      }
      return ft;
    },
    getArchiveFileExtension(att) {
      // Extension badge and icon must not depend on `display_name`,
      // because display_name can be renamed without extension.
      // We prefer the stored original filename, and fall back to MIME type.
      if (!att) return 'FILE';

      const fileName = att.original_filename || att.file_name || '';
      if (fileName) {
        const parts = String(fileName).split('.');
        if (parts.length >= 2) {
          const ext = parts[parts.length - 1].toLowerCase();
          if (ext) {
            return ext.length > 4 ? ext.substring(0, 4).toUpperCase() : ext.toUpperCase();
          }
        }
      }

      const ft = (att.file_type || '').toLowerCase();
      // Basic MIME-type fallbacks for common types.
      if (ft.indexOf('image/') === 0) {
        const subtype = ft.split('/').pop();
        if (subtype) return subtype.length > 4 ? subtype.substring(0, 4).toUpperCase() : subtype.toUpperCase();
        return 'IMG';
      }
      if (ft === 'application/pdf') return 'PDF';

      return 'FILE';
    },
    getArchiveFileIconClass(att) {
      const ft = att && att.file_type ? String(att.file_type).toLowerCase() : '';

      // Strict MIME-type based coloring.
      if (ft === 'application/pdf') return 'archive-file-pdf';

      if (
        ft === 'application/msword' ||
        ft === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        return 'archive-file-doc';
      }

      if (
        ft === 'application/vnd.ms-excel' ||
        ft === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        ft === 'text/csv' ||
        ft === 'application/csv'
      ) {
        return 'archive-file-sheet';
      }

      if (
        ft === 'application/vnd.ms-powerpoint' ||
        ft === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ) {
        return 'archive-file-ppt';
      }

      if (ft.indexOf('image/') === 0) return 'archive-file-image';
      if (ft === 'text/plain') return 'archive-file-txt';

      return 'archive-file-default';
    },
    startInlineRenameAttachmentFolder(folder) {
      if (!folder || !folder.id) return;
      if (!this.canEditAttachmentFolder(folder)) return;
      this.inlineRenamingFolderId = folder.id;
      this.inlineRenamingFolderName = folder.name || '';
      this.$nextTick(() => {
        const refKey = 'folderRenameInput-' + folder.id;
        const el = this.$refs[refKey];
        const input = Array.isArray(el) ? el[0] : el;
        if (input && input.focus) {
          input.focus();
          if (input.select) {
            input.select();
          }
        }
      });
    },
    cancelInlineRenameAttachmentFolder() {
      this.inlineRenamingFolderId = null;
      this.inlineRenamingFolderName = '';
      this.inlineRenamingFolderSaving = false;
    },
    async commitInlineRenameAttachmentFolder(folder) {
      if (this.inlineRenamingFolderSaving) return;
      if (!folder || !folder.id) {
        this.cancelInlineRenameAttachmentFolder();
        return;
      }
      if (!this.inlineRenamingFolderId || String(this.inlineRenamingFolderId) !== String(folder.id)) {
        return;
      }
      const nextName = String(this.inlineRenamingFolderName || '').trim();
      const currentName = String(folder.name || '').trim();
      if (nextName === '') {
        this.inlineRenamingFolderName = currentName;
        this.cancelInlineRenameAttachmentFolder();
        return;
      }
      if (nextName === currentName) {
        this.cancelInlineRenameAttachmentFolder();
        return;
      }
      this.inlineRenamingFolderSaving = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateTaskMasterBoardAttachmentFolder($id: ID!, $name: String!) {
              updateTaskMasterBoardAttachmentFolder(id: $id, name: $name) {
                error
                error_code
                message
                data { id name }
              }
            }
          `,
          variables: { id: folder.id, name: nextName },
        });
        const payload = data && data.updateTaskMasterBoardAttachmentFolder;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to rename folder.');
          this.inlineRenamingFolderName = currentName;
          return;
        }
        if (this.$apollo.queries.taskMasterBoard) {
          this.$apollo.queries.taskMasterBoard.refetch();
        }
        this.cancelInlineRenameAttachmentFolder();
      } catch (error) {
        console.error('Error renaming folder:', error);
        alert('Failed to rename folder. Please try again.');
        this.inlineRenamingFolderName = currentName;
      } finally {
        this.inlineRenamingFolderSaving = false;
      }
    },
    startInlineRenameAttachment(att) {
      if (!att || !att.id) return;
      if (!this.canEditMasterBoardAttachment(att)) return;
      this.inlineRenamingAttachmentId = att.id;
      this.inlineRenamingAttachmentName = this.getAttachmentDisplayName(att);
      this.$nextTick(() => {
        const refKey = 'attachmentRenameInput-' + att.id;
        const el = this.$refs[refKey];
        const input = Array.isArray(el) ? el[0] : el;
        if (input && input.focus) {
          input.focus();
          if (input.select) {
            input.select();
          }
        }
      });
    },
    cancelInlineRenameAttachment() {
      this.inlineRenamingAttachmentId = null;
      this.inlineRenamingAttachmentName = '';
      this.inlineRenamingAttachmentSaving = false;
    },
    async commitInlineRenameAttachment(att) {
      if (this.inlineRenamingAttachmentSaving) return;
      if (!att || !att.id) {
        this.cancelInlineRenameAttachment();
        return;
      }
      if (!this.inlineRenamingAttachmentId || String(this.inlineRenamingAttachmentId) !== String(att.id)) {
        return;
      }
      const nextName = String(this.inlineRenamingAttachmentName || '').trim();
      const currentName = String(this.getAttachmentDisplayName(att) || '').trim();
      if (nextName === '') {
        this.inlineRenamingAttachmentName = currentName;
        this.cancelInlineRenameAttachment();
        return;
      }
      if (nextName === currentName) {
        this.cancelInlineRenameAttachment();
        return;
      }
      this.inlineRenamingAttachmentSaving = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateTaskMasterBoardAttachment($id: ID!, $display_name: String!) {
              updateTaskMasterBoardAttachment(id: $id, display_name: $display_name) {
                error
                error_code
                message
                data { id display_name }
              }
            }
          `,
          variables: { id: att.id, display_name: nextName },
        });
        const payload = data && data.updateTaskMasterBoardAttachment;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to update attachment.');
          this.inlineRenamingAttachmentName = currentName;
          return;
        }
        if (this.$apollo.queries.taskMasterBoard) {
          this.$apollo.queries.taskMasterBoard.refetch();
        }
        this.cancelInlineRenameAttachment();
      } catch (error) {
        console.error('Error updating attachment:', error);
        alert('Failed to update attachment. Please try again.');
        this.inlineRenamingAttachmentName = currentName;
      } finally {
        this.inlineRenamingAttachmentSaving = false;
      }
    },
    async createAttachmentFolder() {
      if (!this.masterBoardData || parseInt(this.masterBoardData.id) === 0) return;
      this.masterBoardFolderCreating = true;
      try {
        const variables = { master_board_id: this.masterBoardData.id };
        if (this.currentAttachmentFolderId) {
          variables.parent_folder_id = this.currentAttachmentFolderId;
        }
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateTaskMasterBoardAttachmentFolder($master_board_id: ID!, $parent_folder_id: ID) {
              createTaskMasterBoardAttachmentFolder(master_board_id: $master_board_id, parent_folder_id: $parent_folder_id) {
                error
                error_code
                message
                data { id name parent_folder_id }
              }
            }
          `,
          variables: variables,
        });
        const payload = data && data.createTaskMasterBoardAttachmentFolder;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to create folder.');
          return;
        }
        if (this.$apollo.queries.taskMasterBoard) {
          this.$apollo.queries.taskMasterBoard.refetch();
        }
      } catch (error) {
        console.error('Error creating folder:', error);
        alert('Failed to create folder. Please try again.');
      } finally {
        this.masterBoardFolderCreating = false;
      }
    },
    // Renaming is handled inline in the grid (no popup).
    async onMasterBoardAttachmentSelected(event) {
      const input = event.target;
      const file = input && input.files && input.files[0] ? input.files[0] : null;
      if (!file || !this.masterBoardData || parseInt(this.masterBoardData.id) === 0) return;
      this.masterBoardUploading = true;
      try {
        const uploadVariables = {
          master_board_id: this.masterBoardData.id,
          file: file,
        };
        if (this.currentAttachmentFolderId) {
          uploadVariables.folder_id = this.currentAttachmentFolderId;
        }
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UploadTaskMasterBoardAttachment($master_board_id: ID!, $folder_id: ID, $file: Upload!) {
              uploadTaskMasterBoardAttachment(master_board_id: $master_board_id, folder_id: $folder_id, file: $file) {
                error
                error_code
                message
                data { id file_name original_filename display_name folder_id }
              }
            }
          `,
          variables: uploadVariables,
        });
        const payload = data && data.uploadTaskMasterBoardAttachment;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to upload attachment.');
          return;
        }
        if (this.$apollo.queries.taskMasterBoard) {
          this.$apollo.queries.taskMasterBoard.refetch();
        }
      } catch (error) {
        console.error('Error uploading attachment:', error);
        alert('Failed to upload attachment. Please try again.');
      } finally {
        this.masterBoardUploading = false;
        if (input) input.value = '';
      }
    },
    isAttachmentFolderEmpty(folderId) {
      if (!folderId) return true;
      const idStr = String(folderId);
      const attachments = this.attachments;
      for (let i = 0; i < attachments.length; i++) {
        const att = attachments[i];
        if (att.folder_id && String(att.folder_id) === idStr) {
          return false;
        }
      }
      const folders = this.attachmentFolders;
      for (let j = 0; j < folders.length; j++) {
        const child = folders[j];
        if (child.parent_folder_id && String(child.parent_folder_id) === idStr) {
          if (!this.isAttachmentFolderEmpty(child.id)) {
            return false;
          }
        }
      }
      return true;
    },
    isAttachmentFolderDownloadable(folder) {
      if (!folder || !folder.id) return false;
      return !this.isAttachmentFolderEmpty(folder.id);
    },
    isFolderInsideTree(folderId, ancestorFolderId) {
      if (!folderId || !ancestorFolderId) return false;
      if (String(folderId) === String(ancestorFolderId)) return true;
      let current = null;
      const folders = this.attachmentFolders;
      for (let i = 0; i < folders.length; i++) {
        if (String(folders[i].id) === String(folderId)) {
          current = folders[i];
          break;
        }
      }
      if (!current || !current.parent_folder_id) return false;
      return this.isFolderInsideTree(current.parent_folder_id, ancestorFolderId);
    },
  },
};
</script>

<style scoped>
.attachment-preview-content {
  min-height: 400px;
}

.attachment-preview-img {
  max-height: 75vh;
}

.attachment-preview-iframe {
  min-height: 75vh;
}

.archive-explorer {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
  overflow: visible;
}

.archive-address-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  padding: 10px 14px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  line-height: 1.4;
}

.archive-address-root,
.archive-address-crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  color: #374151;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.archive-address-root:hover,
.archive-address-crumb:hover {
  background: #eff6ff;
  color: #1d4ed8;
}

.archive-address-sep {
  color: #9ca3af;
  padding: 0 2px;
  user-select: none;
}

.archive-address-current {
  padding: 4px 8px;
  color: #1e3a8a;
  font-weight: 600;
}

.archive-status,
.archive-empty {
  padding: 48px 24px;
  text-align: center;
}

.archive-empty-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  color: #d1d5db;
}

.archive-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
  gap: 4px;
  padding: 16px;
  min-height: 180px;
  position: relative;
}

.archive-marquee {
  position: fixed;
  z-index: 30;
  border: 1px solid rgba(37, 99, 235, 0.7);
  background: rgba(59, 130, 246, 0.18);
  pointer-events: none;
}

@media (min-width: 640px) {
  .archive-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
    padding: 20px;
  }
}

.archive-grid-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: default;
  user-select: none;
  -webkit-touch-callout: none;
  transition: background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
}

.archive-grid-item:hover {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.2);
}

.archive-grid-item-selected {
  background: rgba(59, 130, 246, 0.14);
  border-color: rgba(37, 99, 235, 0.45);
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.15);
}

.archive-grid-item-cut {
  opacity: 0.55;
}

.archive-item-kebab-menu {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  color: #4b5563;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, background-color 0.12s ease, color 0.12s ease;
}

.archive-grid-item:hover .archive-item-kebab-menu,
.archive-grid-item-selected .archive-item-kebab-menu,
.archive-item-kebab-menu:focus,
.archive-item-kebab-menu-active {
  opacity: 1;
  pointer-events: auto;
}

.archive-item-kebab-menu:hover,
.archive-item-kebab-menu:focus {
  background: #f3f4f6;
  color: #111827;
  outline: none;
}

.archive-kebab-icon {
  width: 12px;
  height: 12px;
}

@media (min-width: 640px) {
  .archive-item-kebab-menu {
    display: none;
  }
}

.archive-item-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 64px;
  margin-bottom: 8px;
}

.archive-folder-icon {
  width: 56px;
  height: auto;
  max-height: 52px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.archive-file-icon {
  position: relative;
  width: 48px;
  height: 56px;
  color: #6b7280;
}

.archive-file-icon-svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
}

.archive-file-ext {
  position: absolute;
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  color: #ffffff;
  padding: 2px 4px;
  border-radius: 2px;
  max-width: 36px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archive-file-pdf {
  color: #dc2626;
}

.archive-file-pdf .archive-file-ext {
  background: #dc2626;
}

.archive-file-doc {
  color: #2563eb;
}

.archive-file-doc .archive-file-ext {
  background: #2563eb;
}

.archive-file-sheet {
  color: #16a34a;
}

.archive-file-sheet .archive-file-ext {
  background: #16a34a;
}

.archive-file-ppt {
  color: #ea580c;
}

.archive-file-ppt .archive-file-ext {
  background: #ea580c;
}

.archive-file-image {
  color: #7c3aed;
}

.archive-file-image .archive-file-ext {
  background: #7c3aed;
}

.archive-file-txt {
  color: #4b5563;
}

.archive-file-txt .archive-file-ext {
  background: #4b5563;
}

.archive-file-default {
  color: #6b7280;
}

.archive-file-default .archive-file-ext {
  background: #6b7280;
}

.archive-item-label {
  width: 100%;
  margin: 0;
  padding: 0 2px;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 500;
  color: #1f2937;
  text-align: center;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.archive-item-label-wrap {
  width: 100%;
}

.archive-item-label-input {
  width: 100%;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 500;
  color: #111827;
  text-align: center;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid #93c5fd;
  background: #eff6ff;
  outline: none;
}

.archive-meta-tooltip {
  position: absolute;
  left: 50%;
  top: calc(100% + 8px);
  transform: translateX(-50%);
  width: 220px;
  max-width: 90vw;
  padding: 10px 12px;
  border-radius: 8px;
  background: #111827;
  color: #f9fafb;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  z-index: 20;
}

.archive-meta-tooltip::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent transparent #111827 transparent;
}

.archive-grid-item:hover .archive-meta-tooltip {
  opacity: 1;
  visibility: visible;
}

.archive-meta-title {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: #ffffff;
  word-break: break-word;
}

.archive-meta-rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.archive-meta-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  line-height: 1.35;
}

.archive-meta-label {
  flex-shrink: 0;
  color: #9ca3af;
}

.archive-meta-value {
  text-align: right;
  color: #f3f4f6;
  word-break: break-word;
}

.archive-context-menu {
  position: fixed;
  z-index: 50;
  min-width: 180px;
  padding: 6px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.archive-context-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #111827;
  font-size: 13px;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.archive-context-menu-item:hover {
  background: #f3f4f6;
}

.archive-context-menu-item-danger {
  color: #dc2626;
}

.archive-context-menu-item-danger:hover {
  background: #fef2f2;
}

.archive-context-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.archive-context-menu-icon-svg {
  width: 16px;
  height: 16px;
}

.archive-context-menu-label {
  flex: 1;
}
</style>
